import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  ConfirmDialog,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import {
  paymentGatewayAdminApi,
  PaymentGatewayConfig,
  PaymentGatewayTestResult,
} from "@/services/api";
import {
  Send,
  Plus,
  CheckCircle,
  Loader2,
  Trash2,
  Pencil,
  Zap,
  X,
  Globe,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

const PROVIDER_OPTIONS = [
  { code: "UNBLOCKPAY", name: "UnblockPay", defaultUrl: "https://api.sandbox.unblockpay.com" },
  { code: "CONDUIT", name: "Conduit", defaultUrl: "" },
  { code: "RIPPLE", name: "Ripple Pagamentos", defaultUrl: "" },
];

export function AdminPaymentGatewayPage() {
  const [configs, setConfigs] = useState<PaymentGatewayConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<PaymentGatewayTestResult | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PaymentGatewayConfig | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    providerCode: "UNBLOCKPAY",
    name: "",
    apiKey: "",
    apiSecret: "",
    baseUrl: "https://api.sandbox.unblockpay.com",
    environment: "sandbox",
    isActive: false,
  });

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: "default" | "danger";
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirmar",
    variant: "default",
    onConfirm: () => {},
    loading: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const data = await paymentGatewayAdminApi.listConfigs();
      setConfigs(data);
    } catch (error) {
      console.error("Erro ao carregar configs:", error);
    } finally {
      setLoading(false);
    }
  }

  function openCreateModal() {
    setEditingConfig(null);
    setForm({
      providerCode: "UNBLOCKPAY",
      name: "",
      apiKey: "",
      apiSecret: "",
      baseUrl: "https://api.sandbox.unblockpay.com",
      environment: "sandbox",
      isActive: false,
    });
    setTestResult(null);
    setShowModal(true);
  }

  function openEditModal(config: PaymentGatewayConfig) {
    setEditingConfig(config);
    setForm({
      providerCode: config.providerCode,
      name: config.name,
      apiKey: "",
      apiSecret: "",
      baseUrl: config.baseUrl,
      environment: config.environment,
      isActive: config.isActive,
    });
    setTestResult(null);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingConfig(null);
    setTestResult(null);
  }

  function handleProviderChange(code: string) {
    const provider = PROVIDER_OPTIONS.find((p) => p.code === code);
    setForm({
      ...form,
      providerCode: code,
      name: form.name || (provider?.name ?? ""),
      baseUrl: provider?.defaultUrl || form.baseUrl,
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (editingConfig) {
        const updateData: Record<string, unknown> = {};
        if (form.name) updateData.name = form.name;
        if (form.apiKey) updateData.apiKey = form.apiKey;
        if (form.apiSecret) updateData.apiSecret = form.apiSecret;
        if (form.baseUrl) updateData.baseUrl = form.baseUrl;
        if (form.environment) updateData.environment = form.environment;
        updateData.isActive = form.isActive;
        await paymentGatewayAdminApi.updateConfig(editingConfig.id, updateData);
      } else {
        await paymentGatewayAdminApi.createConfig({
          providerCode: form.providerCode,
          name: form.name || PROVIDER_OPTIONS.find((p) => p.code === form.providerCode)?.name || form.providerCode,
          apiKey: form.apiKey,
          apiSecret: form.apiSecret || undefined,
          baseUrl: form.baseUrl,
          environment: form.environment,
          isActive: form.isActive,
        });
      }
      closeModal();
      await loadData();
    } catch (error) {
      console.error("Erro ao salvar:", error);
    } finally {
      setSaving(false);
    }
  }

  async function handleTest(configId: string) {
    setTestingId(configId);
    setTestResult(null);
    try {
      const result = await paymentGatewayAdminApi.testConfig(configId);
      setTestResult(result);
      await loadData();
    } catch (error) {
      console.error("Erro no teste:", error);
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro desconhecido",
      });
    } finally {
      setTestingId(null);
    }
  }

  function handleDelete(configId: string) {
    setConfirmDialog({
      open: true,
      title: "Excluir Provedor",
      description:
        "Tem certeza que deseja excluir esta configuração de provedor de pagamento? Esta ação é irreversível.",
      confirmText: "Excluir",
      variant: "danger",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await paymentGatewayAdminApi.deleteConfig(configId);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          await loadData();
        } catch (error) {
          console.error("Erro ao excluir:", error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false,
    });
  }

  function getStatusBadge(config: PaymentGatewayConfig) {
    if (!config.isActive) {
      return <Badge variant="secondary">Inativo</Badge>;
    }
    if (config.lastTestStatus === "success") {
      return <Badge variant="success">Conectado</Badge>;
    }
    if (config.lastTestStatus === "error") {
      return <Badge variant="destructive">Erro</Badge>;
    }
    return <Badge variant="warning">Não testado</Badge>;
  }

  function getEnvironmentBadge(env: string) {
    if (env === "production") {
      return <Badge variant="destructive">Produção</Badge>;
    }
    return <Badge variant="secondary">Sandbox</Badge>;
  }

  const activeConfigs = configs.filter((c) => c.isActive).length;
  const connectedConfigs = configs.filter((c) => c.lastTestStatus === "success").length;

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Send className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Pagamentos Globais"
          description="Provedores de pagamento USDC → fiat"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Send className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Pagamentos Globais"
        description="Provedores de pagamento USDC → fiat"
        stats={[
          { value: configs.length, label: "Provedores" },
          { value: activeConfigs, label: "Ativos" },
          { value: connectedConfigs, label: "Conectados" },
        ]}
        actions={
          <Button
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={openCreateModal}
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Provedor
          </Button>
        }
      />

      {/* Configs Section */}
      <h2 className="text-lg font-semibold">Provedores Configurados</h2>

      {configs.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhum provedor de pagamento configurado</p>
            <Button variant="outline" className="mt-4" onClick={openCreateModal}>
              Adicionar primeiro provedor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {configs.map((config) => (
            <Card key={config.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <Send className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium">{config.name}</p>
                      <p className="text-sm text-muted-foreground">{config.providerCode}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getEnvironmentBadge(config.environment)}
                    {getStatusBadge(config)}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">URL Base</span>
                    <span className="text-xs font-mono truncate max-w-[200px]" title={config.baseUrl}>
                      {config.baseUrl}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">API Key</span>
                    <span>{config.hasApiKey ? "••••••••" : "Não configurada"}</span>
                  </div>
                  {config.lastTestedAt && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Último teste</span>
                      <span>{formatDate(config.lastTestedAt)}</span>
                    </div>
                  )}
                  {config.lastTestStatus === "error" && config.lastTestError && (
                    <div className="mt-2 p-2 bg-destructive/10 rounded text-destructive text-xs">
                      {config.lastTestError}
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleTest(config.id)}
                    disabled={testingId === config.id}
                  >
                    {testingId === config.id ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4 mr-1" />
                    )}
                    Testar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEditModal(config)}>
                    <Pencil className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(config.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Test Result Feedback */}
      {testResult && (
        <Card className={testResult.success ? "border-emerald-500/30 bg-emerald-500/5" : "border-destructive/30 bg-destructive/5"}>
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <X className="w-5 h-5 text-destructive shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">
                {testResult.success ? "Conexão bem-sucedida" : "Falha na conexão"}
              </p>
              <p className="text-xs text-muted-foreground">{testResult.message}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setTestResult(null)}>
              <X className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>
                {editingConfig ? "Editar Provedor" : "Novo Provedor de Pagamento"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!editingConfig && (
                <div>
                  <label className="text-sm font-medium">Provedor</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={form.providerCode}
                    onChange={(e) => handleProviderChange(e.target.value)}
                  >
                    {PROVIDER_OPTIONS.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Nome</label>
                <Input
                  placeholder="Ex: UnblockPay Sandbox"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  API Key {editingConfig && "(deixe vazio para manter atual)"}
                </label>
                <Input
                  type="password"
                  placeholder={editingConfig ? "••••••••" : "Cole a API Key aqui"}
                  value={form.apiKey}
                  onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  API Secret (opcional)
                </label>
                <Input
                  type="password"
                  placeholder={editingConfig ? "••••••••" : "Cole o API Secret aqui"}
                  value={form.apiSecret}
                  onChange={(e) => setForm({ ...form, apiSecret: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium">URL Base</label>
                <Input
                  placeholder="https://api.sandbox.unblockpay.com"
                  value={form.baseUrl}
                  onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Ambiente</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={form.environment}
                  onChange={(e) => setForm({ ...form, environment: e.target.value })}
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Produção</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="h-4 w-4 rounded border-input"
                />
                <label htmlFor="isActive" className="text-sm font-medium">
                  Ativo
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={closeModal}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving || (!form.apiKey && !editingConfig)}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingConfig ? "Salvar" : "Criar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        loading={confirmDialog.loading}
      />
    </div>
  );
}
